import BaseButton from 'components/atoms/baseButton';
import BaseTableNew from 'components/atoms/base-table/new';
import { useEffect, type FC } from 'react';
import {
  usePartialsTableDataQueryFilter,
  useConsoleTable,
  useGetPartialsTableData,
  CCM_CONSOLE_PAGE_SIZE,
} from 'infrastructure/hooks/ccm/console';
import { useTranslation } from 'react-i18next';
import {
  checkIsPageAvailable,
  getIsPodFiltered,
} from 'infrastructure/hooks/ccm/console/utils';
import { UserAddOutlined } from '@ant-design/icons';

import CcmConsoleTableBlock from '../table-block';
import { EmptyPodFilterState } from '../empty-pod-filter-state';
import s from './styles.module.scss';
import { useColumns } from './columns';

import type {
  IPartialsTableData,
  IPartialsTableDataFilters,
} from 'infrastructure/interfaces';

interface PartialsTableProps {}

const PartialsTable: FC<PartialsTableProps> = () => {
  const { filter, updateFilter } = usePartialsTableDataQueryFilter();
  const { data, isLoading, isError } = useGetPartialsTableData(filter);
  const columns = useColumns({ filter });
  const { t } = useTranslation();
  const items = filter['p-allExcluded'] ? [] : data?.data || [];
  const total = filter['p-allExcluded'] ? 0 : data?.count || 0;
  const isPodFiltered = getIsPodFiltered(
    filter.podFilter,
    !!filter['p-allExcluded'],
  );

  const { onSort, pushToQueue, rowSelection, disableAddToQueue, rowClassName } =
    useConsoleTable<IPartialsTableDataFilters, IPartialsTableData>({
      filter,
      updateFilter,
      tablePrefix: 'p-',
      excludeSortFields: [
        'p-page',
        'p-allExcluded',
        'p-clinicGuid',
        'p-excludeClinicGuid',
        'podFilter',
        'patientGuid',
      ],
      tableName: 'partials',
    });

  useEffect(() => {
    const isPageExist = checkIsPageAvailable(
      filter['p-page'],
      total,
      items.length,
    );
    if (!isPageExist && !isLoading && filter['p-page'] !== 1) {
      updateFilter({ 'p-page': undefined });
    }
  }, [filter['p-page'], total, isLoading, items.length]);

  return (
    <CcmConsoleTableBlock
      title={t('labels.partials')}
      total={total}
      headerActions={
        <div className={s['select-wrapper']}>
          <BaseButton
            label={t('labels.addToWorkQueue')}
            className={s['add-queue-btn']}
            icon={<UserAddOutlined />}
            onClick={pushToQueue}
            disabled={disableAddToQueue}
          />
        </div>
      }
    >
      {items.length === 0 && isPodFiltered && !isLoading && !isError ? (
        <EmptyPodFilterState />
      ) : (
        <BaseTableNew
          rowKey="guid"
          items={items}
          columns={columns}
          bordered
          loading={isLoading}
          pagination={{
            current: filter['p-page'],
            pageSize: CCM_CONSOLE_PAGE_SIZE,
            total,
            position: ['bottomRight'],
          }}
          onChangePage={(newPage) => {
            updateFilter({ 'p-page': newPage });
          }}
          dataCy="partials-patients-table"
          tableRowSelection={rowSelection}
          onSort={onSort}
          className={s.table}
          rowClassName={rowClassName}
          rowHoverable
          changeOddRowColor={false}
        />
      )}
    </CcmConsoleTableBlock>
  );
};

export default PartialsTable;
